import { __decorate, __metadata } from '../../node_modules/tslib/tslib.es6.js';
import '../../node_modules/lit/node_modules/@lit/reactive-element/reactive-element.js';
import { html as x } from '../../node_modules/lit-html/lit-html.js';
import '../../node_modules/lit-element/lit-element.js';
import { property as n } from '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/property.js';
import { state as t } from '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/state.js';
import '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/query-assigned-elements.js';
import { ifDefined as l } from '../../node_modules/lit-html/directives/if-defined.js';
import getClassName from '../../node_modules/@fmr-ap142296/brick-helpers/dist/src/functions/get-class-name.js';
import { ScopedElementsMixin } from '../utilities/scoped-elements/scoped-elements.mixin.js';
import { PvdSlotifyBaseComponent } from '../pvd-base-component/pvd-slotify-base-component.js';
import { LegacyKeyboardKey, KeyboardKey } from '../pvd-utils/enums/keyboard-key.js';
import { MutationType } from '../pvd-utils/enums/mutation-type.js';
import { debounce } from '../pvd-utils/scripts/debounce.js';
import { FOCUSABLE_SELECTORS } from '../pvd-utils/constants/focusable-selectors.js';
import { getElement } from '../pvd-utils/scripts/get-element.js';

var ModalState;
(function (ModalState) {
    ModalState["closed"] = "closed";
    ModalState["closing"] = "closing";
    ModalState["opened"] = "opened";
    ModalState["opening"] = "opening";
})(ModalState || (ModalState = {}));
const MODAL_WIDTH = { 400: '400', 600: '600', 800: '800' };
const DEFAULT_OPEN = false;
const DEFAULT_STATE = ModalState.closed;
const DEFAULT_CLOSE_BTN_TEXT = 'Close dialog';
const DEFAULT_INTERCEPT_CLOSE = false;
const DEFAULT_WIDTH = MODAL_WIDTH[600];
const WINDOW_RESIZE_DELAY = 150;
/**
 * Providence Modal component.
 * @see {@link https://providence.fmr.com/4.x/components/modal/modal.html|Modal documentation}
 */
class PvdModal extends ScopedElementsMixin(PvdSlotifyBaseComponent) {
    constructor() {
        super(...arguments);
        /**
         * Keeps track of the initial `inert` value of sibling elements
         */
        this._initialInertValues = [];
        /**
         * The initial `padding-right` value of `document.body`
         *
         * @remarks
         * This value is used to reset the `padding-right` of `document.body` when the
         * component is closed
         */
        this._prevBodyPaddingRight = '';
        this.pvdCloseButtonA11yText = DEFAULT_CLOSE_BTN_TEXT;
        this.pvdInterceptClose = DEFAULT_INTERCEPT_CLOSE;
        this.pvdOpen = DEFAULT_OPEN;
        this.pvdModalWidth = DEFAULT_WIDTH;
        this._pvdState = DEFAULT_STATE;
        ////////////////////
        // EVENT HANDLERS //
        ////////////////////
        /**
         * Handles when the Modal is closed
         */
        this.closeModalEventHandler = () => {
            if (this.pvdInterceptClose) {
                const event = new CustomEvent('pvd-modal-close-intercept', {
                    detail: { modalRef: this },
                });
                this.dispatchEvent(event);
            }
            else {
                this.pvdOpen = false;
            }
        };
        /**
         * Handles when the Modal is opened
         */
        this.openModalEventHandler = () => {
            this.pvdOpen = true;
        };
        /**
         * Handles what occurs after the Modal animation is complete
         */
        this.modalAnimationEndEventHandler = () => {
            this.pvdOpen ? this.opened() : this.closed();
            this.removeAnimationEndEvent();
        };
    }
    /////////////////////
    // PRIVATE GETTERS //
    /////////////////////
    static get scopedElements() {
        // $namespace-$name-$extendingComponentName
        return {
            'pvd-scoped-icon': customElements.get(`${this.namespace}-icon`),
        };
    }
    /**
     * Creates a generated id for the modal heading. used if `pvd-aria-labelledby` is not used.
     * @return a generated id to be used for the heading within the modal
     */
    get generatedHeadingId() {
        if (!this._generatedHeadingId) {
            this._generatedHeadingId = `pvd-modal-heading-id-${PvdSlotifyBaseComponent.generateRandomNumber()}`;
        }
        return this._generatedHeadingId;
    }
    /**
     * Creates a generated id for the modal body. used if `pvd-aria-describedby` is not used.
     * @return a generated id to bee used for the body within the modal
     */
    get generatedBodyId() {
        if (!this._generatedBodyId) {
            this._generatedBodyId = `pvd-modal-body-id-${PvdSlotifyBaseComponent.generateRandomNumber()}`;
        }
        return this._generatedBodyId;
    }
    /**
     * The modal's parent element
     *
     * @remarks
     * The value of `parentEl` is either:
     * 1. identical to `pvdParentEl` if `pvdParentEl` is an HTMLElement, OR
     * 2. the result of `document.querySelector` if `pvdParentEl` is a string
     */
    get parentEl() {
        if (!this._parentEl) {
            this._parentEl = getElement(this.pvdParentEl);
        }
        return this._parentEl;
    }
    /**
     * Use to clear the `parentEl` cache whenever `pvdParentEl` is changed
     */
    parentElCacheBust() {
        this._parentEl = undefined;
    }
    /**
     * The element that should receive focus when the modal closes
     *
     * @remarks
     * The value of `returnFocusEl` is either:
     * 1. identical to `pvdReturnFocusEl` if `pvdReturnFocusEl` is an HTMLElement, OR
     * 2. the result of `document.querySelector` if `pvdReturnFocusEl` is a string
     */
    get returnFocusEl() {
        if (!this._returnFocusEl) {
            this._returnFocusEl = getElement(this.pvdReturnFocusEl);
        }
        return this._returnFocusEl;
    }
    /**
     * Use to clear the `returnFocusEl` cache whenever `pvdReturnFocusEl` is changed
     */
    returnFocusElCacheBust() {
        this._returnFocusEl = undefined;
    }
    /**
     * Used to find if there is a header
     */
    hasHeading() {
        return this.isSlotUsed('heading') ? true : false;
    }
    ////////////
    // STATES //
    ////////////
    /**
     * Handles opening/closing state when `pvdOpen` is changed
     */
    openStateChange() {
        if (this.pvdOpen && this._pvdState === ModalState.closed) {
            // if the user wants the modal to open and the modal is not already open, start opening
            this.bindAnimationEndEvent();
            this.bindWindowResizeEvent();
            this.opening();
        }
        else if (!this.pvdOpen &&
            (this._pvdState === ModalState.opened || this._pvdState === ModalState.opening)) {
            // if the user wants the modal to close and the modal is not already closed, start closing
            this.bindAnimationEndEvent();
            this.removeWindowResizeEvent();
            this.closing();
            // put modal in the closed state (without relying on waiting on an animation end event)
            this.modalAnimationEndEventHandler();
        }
    }
    /**
     * Does anything that needs to be done while the modal is 'opening'
     */
    opening() {
        this.hideSiblingsFromAssistiveTechnology();
        this.hidePageOverflow();
        this.captureLastFocusableEl();
        this._pvdState = ModalState.opening;
        this.triggerModalChangeEvent();
    }
    /**
     * Does anything that needs to be done while the modal is 'closing'
     */
    closing() {
        this.resetSiblingInertValues();
        this._pvdState = ModalState.closing;
        this.triggerModalChangeEvent();
    }
    /**
     * Does anything that needs to be done after the modal is 'opened'
     */
    async opened() {
        await this.updateComplete;
        // adds init calculations needed on open
        this.setMaxHeight();
        // initial checks for handling content within Modal dialog
        await this.checkContent();
        // add any mutation observers
        this.addContentChangeObserver();
        // observe existing observers
        this.observeMutations();
        // focus into modal dialog first interactive element
        this.focusFirstModalElement();
        this._pvdState = ModalState.opened;
        this.triggerModalChangeEvent();
    }
    /**
     * Does anything that needs to be done after the modal is 'closed'
     */
    async closed() {
        await this.updateComplete;
        // removes any mutation observers
        this.removeMutationObservers();
        // focuses back to the last active element before modal opened
        this.focusLastActiveElement();
        // resets any `hidden` overflow being set
        this.resetPageOverflow();
        this._pvdState = ModalState.closed;
        this.triggerModalChangeEvent();
    }
    /////////////
    // METHODS //
    /////////////
    /**
     * Listener for `transitionend` when the modal completes its CSS transition
     */
    bindAnimationEndEvent() {
        this._modalEl.addEventListener(this.whichAnimationEvent(), this.modalAnimationEndEventHandler);
    }
    /**
     * Listener for window `resize` when modal is open
     */
    bindWindowResizeEvent() {
        this._resizeHandler = debounce(this.windowResizeHandler.bind(this), WINDOW_RESIZE_DELAY);
        window.addEventListener('resize', this._resizeHandler);
    }
    /**
     * Removes `transitionend` listener
     */
    removeAnimationEndEvent() {
        this._modalEl.removeEventListener(this.whichAnimationEvent(), this.modalAnimationEndEventHandler);
    }
    /**
     * Removes window `resize` listener
     */
    removeWindowResizeEvent() {
        window.removeEventListener('resize', this._resizeHandler);
    }
    /**
     * Creates mutation observer to watch for content changing within the Modal dialog
     */
    addContentChangeObserver() {
        if (!this._contentChangeObserver) {
            this._contentChangeObserver = new MutationObserver((mutationsList) => {
                this.contentChangeMutationEventHandler(mutationsList);
            });
        }
    }
    /**
     * Append the Modal as a child of `pvdParentEl`
     *
     * @remarks
     * Moving the Modal as close as possible to the root of the DOM is necessary
     * to allow `hideSiblingsFromAssistiveTechnology` to have an impact
     */
    appendToParentEl() {
        var _a;
        (_a = this.parentEl) === null || _a === void 0 ? void 0 : _a.appendChild(this);
    }
    /**
     * Apply `inert` to sibling elements to prevent them from being seen by
     * assistive technology when the Modal is open
     */
    hideSiblingsFromAssistiveTechnology() {
        // TODO: remove in PVD 4 when `parentEl` has a default value
        if (!this.parentEl)
            return;
        const siblings = Array.from(this.parentEl.children).filter(el => el !== this);
        // save initial inert values so they can be reset
        this._initialInertValues = siblings.map(sibling => ({
            el: sibling,
            val: sibling.getAttribute('inert'),
        }));
        siblings.forEach(sibling => sibling.setAttribute('inert', ''));
    }
    /**
     * Reset the `inert` attribute of sibling elements to what they were
     * before the Modal was opened
     */
    resetSiblingInertValues() {
        // TODO: remove in PVD 4 when `parentEl` has a default value
        if (!this.parentEl)
            return;
        this._initialInertValues.forEach(initialInertValue => {
            if (initialInertValue.val) {
                initialInertValue.el.setAttribute('inert', '');
            }
            else {
                initialInertValue.el.removeAttribute('inert');
            }
        });
        this._initialInertValues = [];
    }
    /**
     * Captures the internal references for the popover elements
     */
    addElementReferences() {
        this._modalEl = this.querySelector(`.${this.constructor.namespace}-modal-root`);
        this._modalDialogEl = this.querySelector('.pvd-modal__dialog');
        this._modalOverlayEl = this.querySelector('.pvd-modal__overlay');
    }
    /**
     * Sets the first and last focusable elements within the popover
     */
    addFirstLastFocusableElements() {
        // do NOT add await this.updateComplete in this method. IE11 cannot handle the power
        const preliminaryFocusableEls = Array.from(this._modalDialogEl.querySelectorAll(FOCUSABLE_SELECTORS));
        // checks to see if any preliminary focusable el has or is within a display:none or visibility:hidden
        const focusableElements = preliminaryFocusableEls.filter((currEl) => {
            return (currEl.offsetHeight > 0 && getComputedStyle(currEl).visibility.toUpperCase() !== 'HIDDEN');
        });
        if (focusableElements) {
            this._firstFocusableModalEl = focusableElements[0];
            this._lastFocusableModalEl = focusableElements[focusableElements.length - 1];
        }
    }
    /**
     * Gets the `aria-labelledby` value for the modal dialog
     * @return an id that associates to a heading element within the modal
     */
    dialogAriaLabelledby() {
        if (!this.pvdAriaLabelledby && this.pvdAriaLabel) {
            return;
        }
        if (this.hasHeading()) {
            return this.pvdAriaLabelledby ? this.pvdAriaLabelledby : this.generatedHeadingId;
        }
        return;
    }
    /**
     * Handles keydown events in modal dialog
     * @param e, keyboard event
     */
    dialogKeydownEvent(e) {
        switch (e.key) {
            case KeyboardKey.escape:
            case LegacyKeyboardKey.escape:
                e.preventDefault();
                // if key is esc, close the modal
                this.closeModalEventHandler();
            case KeyboardKey.tab:
            case LegacyKeyboardKey.tab:
                // if the key is tab, make sure focus stays trapped
                const activeEl = document.activeElement;
                if (e.shiftKey) {
                    // reverse tabbing
                    if (activeEl === this._firstFocusableModalEl) {
                        e.preventDefault();
                        this.focusLastModalElement();
                    }
                }
                else {
                    if (activeEl === this._lastFocusableModalEl) {
                        e.preventDefault();
                        this.focusFirstModalElement();
                    }
                }
            default:
                return;
        }
    }
    /**
     * Focuses into the modal's dialog
     */
    focusModalDialog() {
        if (this._modalDialogEl) {
            this._modalDialogEl.focus();
        }
    }
    /**
     * Focuses into the first focusable element within the modal
     */
    focusFirstModalElement() {
        if (this._firstFocusableModalEl) {
            this._firstFocusableModalEl.focus();
        }
    }
    /**
     * Focuses into the last focusable element within the modal
     */
    focusLastModalElement() {
        if (this._lastFocusableModalEl) {
            this._lastFocusableModalEl.focus();
        }
    }
    /**
     * Returns focus to the specified element. If no element is specified, returns
     * focus to the element on the document that had focus before the modal was
     * opened
     */
    focusLastActiveElement() {
        if (this.returnFocusEl) {
            this.returnFocusEl.focus();
        }
        else if (this._lastFocusableEl) {
            this._lastFocusableEl.focus();
        }
    }
    /**
     * Gets the last focusable element before the Modal opens, typically the trigger
     */
    captureLastFocusableEl() {
        this._lastFocusableEl = document.activeElement;
    }
    /**
     * Caches the width of the scrollbar as `this._scrollbarWidth`
     */
    setScrollbarWidth() {
        const scrollingEl = document.createElement('div');
        scrollingEl.classList.add('pvd-modal__scrollbar-measure');
        this.renderRoot.appendChild(scrollingEl);
        this._scrollbarWidth = scrollingEl.offsetWidth - scrollingEl.clientWidth;
        this.renderRoot.removeChild(scrollingEl);
    }
    /**
     * Sets `overflow:hidden` to body to prevent background scrolling
     */
    hidePageOverflow() {
        this.lockBackground();
        this._prevBackgroundOverflow = document.body.style.overflow;
        // placebo class to indicate modal is applying global styles
        document.body.classList.add(`${PvdModal.namespace}-modal-open`);
        document.body.style.overflow = 'hidden';
    }
    /**
     * Prevent the background from shifting when overflow is hidden
     */
    lockBackground() {
        if (this.hasVerticalScrollbar) {
            this._prevBodyPaddingRight = getComputedStyle(document.body)['padding-right'];
            document.body.style.paddingRight = `calc(${this._prevBodyPaddingRight} + ${this._scrollbarWidth}px)`;
        }
    }
    /**
     * Determine if a vertical scrollbar is present
     *
     * @see https://tylercipriani.com/blog/2014/07/12/crossbrowser-javascript-scrollbar-detection/
     */
    get hasVerticalScrollbar() {
        return window.innerWidth > document.documentElement.clientWidth;
    }
    /**
     * Reset the changes made by {@link lockBackground}
     */
    unlockBackground() {
        document.body.style.paddingRight = this._prevBodyPaddingRight;
    }
    /**
     * Inspects the bounding boxes of the content and it's wrapper to determine if the content is scrolling
     */
    async isContentOverflowing() {
        const slotArea = this.querySelector('.pvd-modal__body-wrapper');
        // needing to round due to IE11 miscalculations
        const slotScrollHeight = Math.round(slotArea === null || slotArea === void 0 ? void 0 : slotArea.scrollHeight);
        const wrapperRectHeight = Math.round(slotArea === null || slotArea === void 0 ? void 0 : slotArea.getBoundingClientRect()['height']);
        // check if the slotted content is taller than the bounding box
        if (slotScrollHeight && wrapperRectHeight) {
            this.pvdContentOverflow = slotScrollHeight > wrapperRectHeight;
        }
    }
    /**
     * Starts observations for changes on elements
     */
    observeMutations() {
        if (this._contentChangeObserver && this._modalDialogEl) {
            this._contentChangeObserver.observe(this._modalDialogEl, {
                attributes: true,
                childList: true,
                characterData: true,
                subtree: true,
            });
        }
    }
    /**
     * Evaluates the content within the Modal to see if change needs to occur for a11y purposes
     * TODO: make public once more appropriate name is considered
     */
    async checkContent() {
        // this MUST be complete before getting first/last focusable elements
        // if not, it may not count the scroll container as the last focusable el if its just close button and scroll container
        await this.isContentOverflowing();
        this.addFirstLastFocusableElements();
    }
    /**
     * Removes the mutation observers within Modal
     */
    removeMutationObservers() {
        if (this._contentChangeObserver) {
            this._contentChangeObserver.disconnect();
        }
    }
    /**
     * Resets the overflow of body to prevent previous state
     */
    resetPageOverflow() {
        this.unlockBackground();
        document.body.classList.remove(`${PvdModal.namespace}-modal-open`);
        document.body.style.overflow = this._prevBackgroundOverflow;
    }
    /**
     * Sets the `max-height` of the modal dialog each time the modal is opened or resized
     * cannot use `max-height: 100vh` as it has issues in safari mobile
     * https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
     */
    setMaxHeight() {
        const maxHeight = window.innerHeight;
        if (maxHeight && this._modalEl) {
            this._modalDialogEl.style.setProperty('--pvd-modal-dialog-max-height', `${maxHeight}px`);
        }
    }
    /**
     * Overrides z-index token of modal with pvdZIndex when higher z-index is needed
     */
    setZIndex() {
        if (this.pvdZIndex && this._modalEl) {
            this._modalEl.style.zIndex = this.pvdZIndex;
        }
    }
    /**
     * Detecting CSS Transitions Completion with JavaScript - https://davidwalsh.name/css-animation-callback
     * @return a string name of the proper cross-browser animation-end event name
     */
    whichAnimationEvent() {
        const el = document.createElement('fakeelement');
        const animations = {
            animation: 'animationend',
            OAnimation: 'oAnimationEnd',
            MozAnimation: 'animationend',
            WebkitAnimation: 'webkitAnimationEnd',
        };
        for (const a in animations) {
            if (el.style[a] !== undefined) {
                return animations[a];
            }
        }
    }
    ////////////
    // EVENTS //
    ////////////
    /**
     * Event to alert listeners that modal's open state has changed
     */
    triggerModalChangeEvent() {
        const event = new CustomEvent('pvd-modal-change', {
            detail: { state: this._pvdState, open: this.pvdOpen },
        });
        this.dispatchEvent(event);
    }
    /**
     * Mutation Observer callback for when content within Modal gets changed
     *
     * @param mutationsList
     * @param observer
     */
    contentChangeMutationEventHandler(mutationsList) {
        const mutations = mutationsList.filter((mutation) => {
            // needed in order to support the change of an element that updates to display:none
            const validAttributeMutation = mutation.type === MutationType.attributes && mutation.attributeName === 'style';
            return (this.pvdOpen &&
                (mutation.type === MutationType.characterData ||
                    mutation.type === MutationType.childList ||
                    validAttributeMutation));
        });
        if (mutations.length > 0) {
            this.checkContent();
        }
    }
    /**
     * Handles what occurs when the Modal overlay is clicked
     * @param e, click event
     */
    overlayClickEventHandler(e) {
        if (this._modalOverlayEl && e.target === this._modalOverlayEl) {
            this.closeModalEventHandler();
        }
    }
    /**
     * Handles when the window resizes, given the debounce
     */
    windowResizeHandler() {
        this.setMaxHeight();
        this.requestUpdate();
    }
    ////////////////
    // LIFECYCLES //
    ////////////////
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeMutationObservers();
        if (this.pvdOpen) {
            /**
             * When someone closes the component, `openStateChange` is triggered and
             * will call `closing` and `closed` when the close animation starts and
             * ends, respectively.
             *
             * In this case, the component was removed from the DOM while it was
             * still open, so the `openStateChange` method will not be triggered, and
             * we need to call `closing` and `closed` ourselves.
             */
            this.closing();
            this.closed();
        }
    }
    firstUpdated() {
        super.firstUpdated();
        this.addElementReferences();
        this.setScrollbarWidth();
    }
    updated(changedProperties) {
        super.updated(changedProperties);
        // to handle when user tries to close the modal while it's still opening https://jira.fmr.com/browse/PUUCP-2253
        const isEdgeCase = !this.pvdOpen && changedProperties.get('_pvdState') === ModalState.opening;
        if (changedProperties.has('pvdOpen') || isEdgeCase) {
            try {
                this.openStateChange();
            }
            catch (e) {
                // we leave this empty because it can fire before the component has finished setting up so we need to ignore that case
            }
        }
        if (changedProperties.has('pvdZIndex')) {
            this.setZIndex();
        }
        if (changedProperties.has('pvdParentEl')) {
            this.parentElCacheBust();
            this.appendToParentEl();
        }
        if (changedProperties.has('pvdReturnFocusEl')) {
            this.returnFocusElCacheBust();
        }
    }
    ////////////
    // RENDER //
    ////////////
    renderBody() {
        return x `<div class="pvd-modal__body-wrapper" id="${l(this._generatedBodyId)}" role="${l(this.pvdContentOverflow ? 'region' : undefined)}" tabindex="${l(this.pvdContentOverflow ? '0' : undefined)}"><s-slot></s-slot></div>`;
    }
    renderCloseButton() {
        return x `<button class="pvd-modal__close-button" @click="${this.closeModalEventHandler}" type="button"><span class="pvd-modal__close-button-a11y-text">${this.pvdCloseButtonA11yText}</span> ${this.renderCloseIcon()}</button>`;
    }
    renderCloseIcon() {
        return this
            .scopeTemplate `<pvd-scoped-icon pvd-name="action__close" pvd-size="small"></pvd-scoped-icon>`;
    }
    renderAriaLabel() {
        if (this.pvdAriaLabel && this.pvdAriaLabel != '' && this.pvdAriaLabel != undefined) {
            return this.pvdAriaLabel;
        }
        else if (this.hasHeading() == true) {
            return;
        }
        else
            return 'Information';
    }
    renderHeading() {
        return x `${this.isSlotUsed('heading')
            ? x `<div class="pvd-modal__heading-wrapper" id="${l(this._generatedHeadingId)}"><s-slot name="heading"></s-slot></div>`
            : x `<s-slot name="heading"></s-slot>`}`;
    }
    render() {
        const classMap = {
            default: `${this.constructor.namespace}-modal-root`,
            modifierPrefix: 'pvd-modal',
            modifiers: [
                {
                    condition: this.pvdContentOverflow,
                    className: 'content-overflow',
                },
                {
                    condition: this.pvdModalWidth !== undefined,
                    className: `width-${MODAL_WIDTH[this.pvdModalWidth] ? this.pvdModalWidth : DEFAULT_WIDTH}`,
                },
                {
                    condition: this._pvdState !== undefined,
                    className: this._pvdState,
                },
            ],
        };
        return x `<div class="${getClassName(classMap)}"><div aria-hidden="true" class="pvd-modal__overlay" @click="${this.overlayClickEventHandler}"></div><div aria-label="${this.renderAriaLabel()}" aria-labelledby="${l(this.dialogAriaLabelledby())}" aria-modal="true" class="pvd-modal__dialog" pvd-a11y-modal-title="${l(this.pvdAriaLabel)}" role="dialog" @keydown="${this.dialogKeydownEvent}"><div class="pvd-modal__content" role="document">${this.renderCloseButton()} ${this.renderHeading()} ${this.renderBody()}</div></div></div>`;
    }
}
//#region Component configuration
PvdModal.componentName = 'modal';
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-aria-describedby' }),
    __metadata("design:type", String)
], PvdModal.prototype, "pvdAriaDescribedby", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-aria-label' }),
    __metadata("design:type", String)
], PvdModal.prototype, "pvdAriaLabel", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-aria-labelledby' }),
    __metadata("design:type", String)
], PvdModal.prototype, "pvdAriaLabelledby", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-close-button-a11y-text',
    }),
    __metadata("design:type", String)
], PvdModal.prototype, "pvdCloseButtonA11yText", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-intercept-close',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-intercept-close'),
    }),
    __metadata("design:type", Boolean)
], PvdModal.prototype, "pvdInterceptClose", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-open',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-open'),
    }),
    __metadata("design:type", Boolean)
], PvdModal.prototype, "pvdOpen", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-modal-width',
    }),
    __metadata("design:type", String)
], PvdModal.prototype, "pvdModalWidth", void 0);
__decorate([
    n({
        type: HTMLElement,
        reflect: false,
        attribute: 'pvd-parent-el',
    })
    // TODO: default to `document.body` in PVD 4
    ,
    __metadata("design:type", Object)
], PvdModal.prototype, "pvdParentEl", void 0);
__decorate([
    n({
        type: HTMLElement,
        reflect: false,
        attribute: 'pvd-return-focus-el',
    }),
    __metadata("design:type", Object)
], PvdModal.prototype, "pvdReturnFocusEl", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-z-index',
    }),
    __metadata("design:type", String)
], PvdModal.prototype, "pvdZIndex", void 0);
__decorate([
    t(),
    __metadata("design:type", Boolean)
], PvdModal.prototype, "pvdContentOverflow", void 0);
__decorate([
    t(),
    __metadata("design:type", String)
], PvdModal.prototype, "_pvdState", void 0);

export { DEFAULT_CLOSE_BTN_TEXT, DEFAULT_INTERCEPT_CLOSE, DEFAULT_OPEN, DEFAULT_STATE, DEFAULT_WIDTH, MODAL_WIDTH, ModalState, PvdModal, WINDOW_RESIZE_DELAY };
//# sourceMappingURL=pvd-modal.js.map
